import { Helmet } from 'react-helmet-async';
import Screen from '../Layouts/Screen/Screen';
import {
    AlertTriangle,
    Check,
    Key,
    Lock,
    MessageCircle,
    Shield,
    Smartphone,
} from 'react-feather';
import { useEffect, useRef, useState } from 'react';
import { useAuth, useMessage } from '../../global-components/src';
import { errorMessage } from '../../global-components/src/utils/text';
import { api } from '../../services/api';
import { canManageAccountSecurity } from '../../utils/account-security';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import i18n from '../../global-components/src/i18n';
import './style.css';

type DisableTarget = 'authenticator' | 'pacoca_chat';

type TwoFactorMethods = {
    authenticator_enabled: boolean;
    pacoca_chat_enabled: boolean;
    pacoca_chat_available: boolean;
};

const Activate2fa = () => {
    const { token, user, setUser } = useAuth();
    const canManageSecurity = canManageAccountSecurity(user?.created_at);
    const { setMessage } = useMessage();
    const [loading, setLoading] = useState(false);
    const [methods, setMethods] = useState<TwoFactorMethods>({
        authenticator_enabled: !!user?.google2fa_enabled,
        pacoca_chat_enabled: false,
        pacoca_chat_available: false,
    });
    const [qrCode, setQrCode] = useState<string | null>(null);
    const [code, setCode] = useState('');
    const [chatCode, setChatCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
    const [showAuthenticatorSetup, setShowAuthenticatorSetup] = useState(false);
    const [showChatSetup, setShowChatSetup] = useState(false);
    const [disableTarget, setDisableTarget] = useState<DisableTarget | null>(null);
    const [password, setPassword] = useState('');
    const closeRef = useRef<HTMLButtonElement | null>(null);
    const [errorModal, setErrorModal] = useState('');

    const enabledMethodCount = Number(methods.authenticator_enabled) + Number(methods.pacoca_chat_enabled);
    const hasActiveMethod = enabledMethodCount > 0;

    const loadMethods = async () => {
        if (!canManageSecurity) return;

        try {
            const { data } = await api(token).get('/2fa/methods');
            setMethods({
                authenticator_enabled: Boolean(data?.authenticator_enabled),
                pacoca_chat_enabled: Boolean(data?.pacoca_chat_enabled),
                pacoca_chat_available: Boolean(data?.pacoca_chat_available),
            });
        } catch (err) {
            setMessage(errorMessage(err), 'error');
        }
    };

    useEffect(() => {
        if (canManageSecurity) {
            loadMethods();
        }
    }, [canManageSecurity, token]);

    const closeAuthenticatorSetup = () => {
        setShowAuthenticatorSetup(false);
        setQrCode(null);
        setCode('');
    };

    const closeChatSetup = () => {
        setShowChatSetup(false);
        setChatCode('');
    };

    const handleGenerateAuthenticator = async () => {
        try {
            setLoading(true);
            const response = await api(token).post('/2fa/generate');
            setQrCode(response.data.qr);
            setShowChatSetup(false);
            setShowAuthenticatorSetup(true);
        } catch (err) {
            setMessage(errorMessage(err), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEnableAuthenticator = async () => {
        if (!code.trim()) {
            setMessage(i18n.t('settings.2fa.enterCode'), 'error');
            return;
        }

        try {
            setLoading(true);
            const response = await api(token).post('/2fa/enable', { code: code.trim() });
            setUser((previous: any) => ({ ...previous, google2fa_enabled: true }));
            setMethods(previous => ({ ...previous, authenticator_enabled: true }));
            setRecoveryCodes(response.data.recovery_codes || response.data.codes || []);
            closeAuthenticatorSetup();
            setMessage(i18n.t('settings.2fa.activatedSuccess'), 'success');
        } catch (err) {
            setMessage(errorMessage(err), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleBeginChatSetup = async () => {
        try {
            setLoading(true);
            await api(token).post('/2fa/pacoca-chat/enable');
            closeAuthenticatorSetup();
            setShowChatSetup(true);
            setChatCode('');
            setMessage('Enviamos um código para a conversa Paçoca Segurança no Paçoca Papo.', 'success');
        } catch (err) {
            setMessage(errorMessage(err), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmChatSetup = async () => {
        if (!/^\d{8}$/.test(chatCode.trim())) {
            setMessage('Digite o código de 8 dígitos enviado no Paçoca Papo.', 'error');
            return;
        }

        try {
            setLoading(true);
            await api(token).post('/2fa/pacoca-chat/confirm', { code: chatCode.trim() });
            setMethods(previous => ({ ...previous, pacoca_chat_enabled: true }));
            closeChatSetup();
            setMessage('Paçoca Papo ativado como verificação em duas etapas.', 'success');
        } catch (err) {
            setMessage(errorMessage(err), 'error');
        } finally {
            setLoading(false);
        }
    };

    const closeConfirmation = () => {
        setPassword('');
        setErrorModal('');
        setDisableTarget(null);
    };

    const handleDisable = async () => {
        if (!disableTarget) return;

        try {
            setLoading(true);
            await api(token).post(
                disableTarget === 'authenticator' ? '/2fa/disable' : '/2fa/pacoca-chat/disable',
                { password },
            );
            if (disableTarget === 'authenticator') {
                setUser((previous: any) => ({ ...previous, google2fa_enabled: false }));
                setMethods(previous => ({ ...previous, authenticator_enabled: false }));
                setRecoveryCodes([]);
            } else {
                setMethods(previous => ({ ...previous, pacoca_chat_enabled: false }));
            }
            setMessage('Método de verificação removido.', 'success');
            closeConfirmation();
        } catch (err) {
            setErrorModal(errorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const openDisable = (target: DisableTarget) => {
        setErrorModal('');
        setDisableTarget(target);
    };

    const protectionDescription = !hasActiveMethod
        ? 'Escolha pelo menos um método abaixo para confirmar novos acessos.'
        : enabledMethodCount === 1
            ? 'Você tem 1 método de verificação ativo.'
            : 'Você tem 2 métodos de verificação ativos.';

    if (!canManageSecurity) {
        return (
            <>
                <Helmet>
                    <title>Paçoca - Segurança</title>
                </Helmet>
                <Screen paddingTopTitle={true} title={i18n.t('settings.2fa.title')}>
                    <main className="fa2-page">
                        <section className="fa2-overview fa2-overview--pending">
                            <div className="fa2-overview__icon" aria-hidden="true">
                                <Lock size={22} />
                            </div>
                            <div className="fa2-overview__content">
                                <h1>{i18n.t('settings.2fa.title')}</h1>
                                <p>{i18n.t('settings.securityRestricted')}</p>
                            </div>
                        </section>
                    </main>
                </Screen>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>Paçoca - Segurança</title>
            </Helmet>

            <ConfirmationModal
                show={disableTarget !== null}
                onClose={closeConfirmation}
                onConfirm={handleDisable}
                title={disableTarget === 'pacoca_chat'
                    ? 'Remover Paçoca Papo'
                    : i18n.t('settings.2fa.deactivateTitle')}
                text={disableTarget === 'pacoca_chat'
                    ? 'Confirme sua senha para remover o Paçoca Papo da verificação em duas etapas.'
                    : i18n.t('settings.2fa.deactivateText')}
                requirePassword={true}
                setPassword={setPassword}
                errorMessage={errorModal}
                isLoading={loading}
                coverMobile={false}
                closeRef={closeRef}
                idModal="remove-two-factor-method"
            />

            <Screen paddingTopTitle={true} title={i18n.t('settings.2fa.title')}>
                <main className="fa2-page">
                    <section className={`fa2-overview ${hasActiveMethod ? 'fa2-overview--secured' : 'fa2-overview--pending'}`} aria-labelledby="fa2-overview-title">
                        <div className="fa2-overview__icon" aria-hidden="true">
                            {hasActiveMethod ? <Check size={22} /> : <Shield size={22} />}
                        </div>
                        <div className="fa2-overview__content">
                            <p className="fa2-eyebrow">Segurança da conta</p>
                            <h1 id="fa2-overview-title">{hasActiveMethod ? 'Sua conta está protegida' : 'Proteja sua conta'}</h1>
                            <p>{protectionDescription}</p>
                        </div>
                        <span className="fa2-overview__status">
                            {hasActiveMethod ? 'Ativada' : 'Pendente'}
                        </span>
                    </section>

                    <section className="fa2-methods" aria-labelledby="fa2-methods-title">
                        <div className="fa2-section-heading">
                            <div>
                                <p className="fa2-eyebrow">Métodos de verificação</p>
                                <h2 id="fa2-methods-title">Como confirmar seus acessos</h2>
                            </div>
                            <p>Ative os métodos que você quer usar ao entrar na sua conta.</p>
                        </div>

                        <article className={`fa2-method-card ${methods.authenticator_enabled ? 'fa2-method-card--active' : ''}`}>
                            <div className="fa2-method-card__top">
                                <div className="fa2-method-card__icon" aria-hidden="true">
                                    <Smartphone size={21} />
                                </div>
                                <div className="fa2-method-card__content">
                                    <div className="fa2-method-card__title-row">
                                        <h3>Aplicativo autenticador</h3>
                                        <span className={`fa2-status ${methods.authenticator_enabled ? 'fa2-status--active' : ''}`}>
                                            {methods.authenticator_enabled ? 'Ativo' : 'Não configurado'}
                                        </span>
                                    </div>
                                    <p>Use os códigos temporários gerados no seu aplicativo autenticador.</p>
                                </div>
                            </div>

                            <div className="fa2-method-card__actions">
                                {methods.authenticator_enabled ? (
                                    <button className="fa2-button fa2-button--danger" type="button" disabled={loading} onClick={() => openDisable('authenticator')}>
                                        Remover método
                                    </button>
                                ) : (
                                    <button className="fa2-button fa2-button--primary" type="button" disabled={loading} onClick={handleGenerateAuthenticator}>
                                        Configurar aplicativo
                                    </button>
                                )}
                            </div>

                            {showAuthenticatorSetup && qrCode && (
                                <div className="fa2-setup fa2-authenticator-setup" aria-live="polite">
                                    <div className="fa2-qr-wrap">
                                        <img
                                            className="fa2-qr"
                                            src={`data:image/svg+xml;base64,${qrCode}`}
                                            alt="QR Code para configurar a autenticação em duas etapas"
                                        />
                                    </div>
                                    <div className="fa2-setup__content">
                                        <p className="fa2-setup__eyebrow">Configuração em 2 etapas</p>
                                        <h4>Escaneie o QR Code</h4>
                                        <ol className="fa2-setup__steps">
                                            <li>Abra seu aplicativo autenticador.</li>
                                            <li>Escaneie o QR Code e informe o código de 6 dígitos gerado.</li>
                                        </ol>
                                        <label className="fa2-code-label" htmlFor="authenticator-code">Código de confirmação</label>
                                        <input
                                            id="authenticator-code"
                                            className="fa2-code-input"
                                            value={code}
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            pattern="[0-9]*"
                                            maxLength={6}
                                            onChange={event => setCode(event.target.value.replace(/\D/g, ''))}
                                            placeholder="000000"
                                        />
                                        <div className="fa2-setup__actions">
                                            <button className="fa2-button fa2-button--primary" type="button" disabled={loading} onClick={handleEnableAuthenticator}>
                                                {loading ? 'Confirmando…' : 'Confirmar e ativar'}
                                            </button>
                                            <button className="fa2-button fa2-button--quiet" type="button" disabled={loading} onClick={closeAuthenticatorSetup}>
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </article>

                        <article className={`fa2-method-card ${methods.pacoca_chat_enabled ? 'fa2-method-card--active' : ''}`}>
                            <div className="fa2-method-card__top">
                                <div className="fa2-method-card__icon" aria-hidden="true">
                                    <MessageCircle size={21} />
                                </div>
                                <div className="fa2-method-card__content">
                                    <div className="fa2-method-card__title-row">
                                        <h3>Paçoca Papo</h3>
                                        <span className={`fa2-status ${methods.pacoca_chat_enabled ? 'fa2-status--active' : ''}`}>
                                            {methods.pacoca_chat_enabled ? 'Ativo' : 'Não configurado'}
                                        </span>
                                    </div>
                                    <p>Receba um código na conversa Paçoca Segurança e uma notificação no dispositivo.</p>
                                </div>
                            </div>

                            {!methods.pacoca_chat_available && !methods.pacoca_chat_enabled && (
                                <div className="fa2-availability-note" role="status">
                                    <Lock size={16} aria-hidden="true" />
                                    <span>Abra o Paçoca Papo e permita notificações neste dispositivo para usar este método.</span>
                                </div>
                            )}

                            <div className="fa2-method-card__actions">
                                {methods.pacoca_chat_enabled ? (
                                    <button className="fa2-button fa2-button--danger" type="button" disabled={loading} onClick={() => openDisable('pacoca_chat')}>
                                        Remover método
                                    </button>
                                ) : (
                                    <button className="fa2-button fa2-button--primary" type="button" disabled={loading || !methods.pacoca_chat_available} onClick={handleBeginChatSetup}>
                                        Ativar com Paçoca Papo
                                    </button>
                                )}
                            </div>

                            {showChatSetup && (
                                <div className="fa2-setup fa2-chat-setup" aria-live="polite">
                                    <div className="fa2-setup__content">
                                        <p className="fa2-setup__eyebrow">Confirme o Paçoca Papo</p>
                                        <h4>Digite o código enviado</h4>
                                        <ol className="fa2-setup__steps">
                                            <li>Abra a conversa <strong>Paçoca Segurança</strong> no Paçoca Papo.</li>
                                            <li>Digite abaixo o código de 8 dígitos recebido.</li>
                                        </ol>
                                        <label className="fa2-code-label" htmlFor="pacoca-chat-code">Código de confirmação</label>
                                        <input
                                            id="pacoca-chat-code"
                                            className="fa2-code-input"
                                            value={chatCode}
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            pattern="[0-9]*"
                                            maxLength={8}
                                            onChange={event => setChatCode(event.target.value.replace(/\D/g, ''))}
                                            placeholder="00000000"
                                        />
                                        <div className="fa2-setup__actions">
                                            <button className="fa2-button fa2-button--primary" type="button" disabled={loading} onClick={handleConfirmChatSetup}>
                                                {loading ? 'Confirmando…' : 'Confirmar e ativar'}
                                            </button>
                                            <button className="fa2-button fa2-button--quiet" type="button" disabled={loading} onClick={handleBeginChatSetup}>
                                                Enviar outro código
                                            </button>
                                            <button className="fa2-button fa2-button--quiet" type="button" disabled={loading} onClick={closeChatSetup}>
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </article>
                    </section>

                    {recoveryCodes.length > 0 && (
                        <section className="fa2-recovery" aria-labelledby="fa2-recovery-title">
                            <div className="fa2-recovery__heading">
                                <AlertTriangle size={20} aria-hidden="true" />
                                <div>
                                    <h2 id="fa2-recovery-title">Guarde seus códigos de recuperação</h2>
                                    <p>{i18n.t('settings.2fa.recoveryCodesHint')}</p>
                                </div>
                            </div>
                            <div className="fa2-recovery__codes">
                                {recoveryCodes.map((item, index) => (
                                    <code key={index}>{item}</code>
                                ))}
                            </div>
                        </section>
                    )}

                    <details className="fa2-help">
                        <summary>
                            <Key size={17} aria-hidden="true" />
                            <span>Como o Paçoca Papo funciona</span>
                        </summary>
                        <p>O push não mostra o código. Abra o Paçoca Papo, confira a tentativa na conversa Paçoca Segurança e use o código de uso único no login.</p>
                    </details>
                </main>
            </Screen>
        </>
    );
};

export default Activate2fa;
